import { Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { UserStoryService, AgentChatResponse, AgentChatSource } from '../../../../shared/templateGen/template-api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs/operators';
import { marked } from 'marked';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  renderedHtml?: SafeHtml;
  toolsUsed?: string[];
  sources?: AgentChatSource[];
  debug?: any;
  timestamp: Date;
}

@Component({
  selector: 'app-knowledge-base-chat',
  templateUrl: './knowledge-base-chat.component.html',
  styleUrls: ['./knowledge-base-chat.component.scss']
})
export class KnowledgeBaseChatComponent implements OnInit {
  @Input() projectId!: string;
  @Input() currentUserEmail!: string;
  @Input() model: string = 'GPT-5.4 mini';
  @Input() initialQuery: string = '';
  @Input() maxIterations: number = 20;

  @Output() closeChat = new EventEmitter<void>();
  @Output() openSource = new EventEmitter<AgentChatSource>();

  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  @ViewChild('debugModal') private debugModalTemplate!: TemplateRef<any>;

  messages: ChatMessage[] = [];
  inputControl: string = '';
  isGenerating: boolean = false;
  activeDebug: any = null;
  debugCopied: boolean = false;

  constructor(
    private userStoryApi: UserStoryService,
    private sanitizer: DomSanitizer,
    private modalService: NgbModal
  ) {
    marked.setOptions({ breaks: true, gfm: true });
  }

  ngOnInit(): void {
    if (this.initialQuery) {
      this.messages.push({
        role: 'user',
        content: this.initialQuery,
        timestamp: new Date()
      });
      this.runAgentChat(this.initialQuery);
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage(): void {
    const query = this.inputControl.trim();
    if (!query || this.isGenerating) return;

    this.messages.push({ role: 'user', content: query, timestamp: new Date() });
    this.inputControl = '';
    this.scrollToBottom();
    this.runAgentChat(query);
  }

  openSourceDocument(source: AgentChatSource): void {
    this.openSource.emit(source);
  }

  openDebugModal(msg: ChatMessage): void {
    this.activeDebug = msg.debug;
    this.debugCopied = false;
    this.modalService.open(this.debugModalTemplate, {
      size: 'lg', centered: true, scrollable: true
    });
  }

  copyDebug(): void {
    if (!this.activeDebug) return;
    const text = JSON.stringify(this.activeDebug, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      this.debugCopied = true;
      setTimeout(() => this.debugCopied = false, 2000);
    });
  }

  cleanSnippet(snippet: string): string {
    if (!snippet) return '';
    return snippet
      .replace(/---\s*Table\s*(Start|End)\s*---/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private renderMarkdown(text: string): SafeHtml {
    const html = marked.parse(text) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  clearChat(): void {
    this.messages = [];
  }

  private runAgentChat(query: string): void {
    this.isGenerating = true;

    // Build conversation history from previous messages (text only, no sources/debug)
    const history = this.messages
      .slice(0, -1)  // exclude the current user message (already sent as `query`)
      .filter(m => m.content)
      .map(m => ({ role: m.role, content: m.content }));

    this.userStoryApi.agentChat(this.projectId, query, this.model, this.maxIterations, history)
      .pipe(take(1))
      .subscribe({
        next: (response: AgentChatResponse) => {
          this.isGenerating = false;

          const toolsUsed = response.tool_calls_log?.map(t => t.tool) || [];

          this.messages.push({
            role: 'assistant',
            content: response.answer,
            renderedHtml: this.renderMarkdown(response.answer),
            toolsUsed: toolsUsed,
            sources: response.sources || [],
            debug: response.debug || null,
            timestamp: new Date()
          });

          this.scrollToBottom();
        },
        error: (err) => {
          this.isGenerating = false;
          console.error("Agent Chat Error:", err);

          const errorMsg = "Sorry, I encountered an error connecting to the AI.";
          this.messages.push({
            role: 'assistant',
            content: errorMsg,
            renderedHtml: this.renderMarkdown(errorMsg),
            timestamp: new Date()
          });

          this.scrollToBottom();
        }
      });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      requestAnimationFrame(() => {
        if (this.scrollContainer) {
          const el = this.scrollContainer.nativeElement;
          el.scrollTop = el.scrollHeight;
        }
      });
    }, 50);
  }
}
